import React from "react";
import AccordionSection from "./AccordionSection";

const BRANCH_FLOW = `master (producao)
  ^
release/* (preparacao de versao)
  ^
develop (integracao opcional)
  ^
feature/* | bugfix/* | qa/* | hotfix/*`;

const COMMIT_FORMAT = `tipo(escopo): resumo no imperativo`;

const COMMIT_BODY_TEMPLATE = `Contexto: objetivo do commit em 1-2 linhas

Mudancas: lista objetiva com os principais arquivos/impactos

Subtasks: lista curta do que foi executado para entregar o objetivo

Validacao: comandos ou checks executados`;

const COMMIT_EXAMPLES = `fix(api): corrigir exportacao do emailController em CommonJS
build(front): ajustar script de start para ambiente local
docs(docs): alinhar ia-mds ao contexto real do projeto
refactor(repo): mover app React para pasta frontend`;

const DETAILED_COMMIT_TEMPLATE = `fix(api): corrigir fluxo de envio de e-mail no backend

Contexto:
- backend quebrava na inicializacao por incompatibilidade de modulo.

Mudancas:
- ajusta exportacao em backend/controllers/emailController.js
- mantem rota POST /api/send-email sem mudar contrato

Subtasks:
- substituir export default por module.exports
- revisar uso em backend/routes/emailRoutes.js

Validacao:
- backend inicia sem erro de sintaxe
- rota de envio responde com status esperado`;

const QUALITY_COMMANDS = `# frontend
cd frontend
npm install
npm test
npm run build

# backend
cd ../backend
npm install
npm start`;

const mainBranches = [
  { branch: "master", description: "Branch de producao. Recebe merges validados. Hotfix critico pode sair direto daqui." },
  { branch: "release/*", description: "Criado para preparar versoes de producao. Consolidado apos validacao funcional." },
  { branch: "develop", description: "Integracao opcional de features/bugfixes antes de promover para release/master." },
];

const workBranches = [
  { branch: "feature/[id]", description: "Nova funcionalidade.", example: "feature/landing-seo-update" },
  { branch: "bugfix/[id]", description: "Correcao de problema nao urgente.", example: "bugfix/player-mobile-state" },
  { branch: "hotfix/[id]", description: "Correcao critica em producao.", example: "hotfix/email-controller-export" },
  { branch: "qa/[id]", description: "Ajustes de teste, automacao e validacao.", example: "qa/backend-smoke-checklist" },
];

const mergeRules = [
  "Toda mudanca deve passar por PR.",
  "Merges para producao devem ter validacao funcional minima.",
  "Evitar PR gigante misturando temas nao relacionados.",
  "Hotfix de producao deve ser sincronizado com branches ativas.",
  "Documentacao deve acompanhar mudancas estruturais.",
];

const cycleSteps = [
  "Criar branch de trabalho.",
  "Implementar alteracoes pequenas e verificaveis.",
  "Abrir PR.",
  "Validar comportamento.",
  "Merge para branch alvo.",
  "Promover para master quando pronto.",
];

const commitTypes = [
  ["feat", "Nova funcionalidade"],
  ["fix", "Correcao de bug ou comportamento"],
  ["docs", "Documentacao"],
  ["test", "Testes automatizados"],
  ["refactor", "Refatoracao sem alterar comportamento"],
  ["perf", "Melhoria de performance"],
  ["style", "Formatacao (sem alteracao de logica)"],
  ["build", "Build, dependencias externas"],
  ["chore", "Manutencao interna"],
  ["revert", "Reversao de commit anterior"],
];

const commitScopes = [
  ["api", "Backend Node/Express/GraphQL"],
  ["front", "Frontend React"],
  ["docs", "Documentacao"],
  ["infra", "Deploy e configuracoes"],
  ["repo", "Organizacao estrutural"],
  ["seo", "Metadados, canonical, hreflang"],
  ["i18n", "Conteudo e localizacao"],
];

const preCommitChecklist = [
  "git status sem arquivos temporarios indevidos.",
  "Mudancas conferidas com git diff --staged.",
  "Mensagem no padrao tipo(escopo): resumo.",
  "Build/execucao minima validada para area alterada.",
  "Docs atualizadas quando houver mudanca estrutural.",
];

function GitflowTable({ headers, rows }) {
  return (
    <div className="gitflowTableWrap">
      <table className="gitflowTable">
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]}>
              <td>{row[0]}</td>
              <td>{row[1]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function GitflowWindowContent() {
  const [openSection, setOpenSection] = React.useState("branchFlow");

  return (
    <div className="gitflowContent">
      <p className="resumeWindowTitle">Git Flow e Conventional Commits</p>
      <p>Guia didatico consolidado para organizar branches, merges e padrao de commits do repositorio.</p>

      
        <AccordionSection
          sectionId="branchFlow"
          openSection={openSection}
          onOpenSection={setOpenSection}
          title="1. Fluxo de Branches"
          className="gitflowSection methodAccordionItem"
        >
          <p className="gitflowSectionSubtitle">Estrutura principal</p>
          <pre className="gitflowCodeBlock">{BRANCH_FLOW}</pre>
        </AccordionSection>
      

      
        <AccordionSection
          sectionId="mainBranches"
          openSection={openSection}
          onOpenSection={setOpenSection}
          title="2. Branches Principais"
          className="gitflowSection methodAccordionItem"
        >
          <div className="gitflowCardGrid">
            {mainBranches.map((item) => (
              <article key={item.branch} className="gitflowCard">
                <p className="gitflowCardTitle">{item.branch}</p>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </AccordionSection>
      

      
        <AccordionSection
          sectionId="workBranches"
          openSection={openSection}
          onOpenSection={setOpenSection}
          title="3. Branches de Trabalho"
          className="gitflowSection methodAccordionItem"
        >
          <div className="gitflowCardGrid">
            {workBranches.map((item) => (
              <article key={item.branch} className="gitflowCard">
                <p className="gitflowCardTitle">{item.branch}</p>
                <p>{item.description}</p>
                <p className="gitflowExample">Exemplo: {item.example}</p>
              </article>
            ))}
          </div>
        </AccordionSection>
      

      
        <AccordionSection
          sectionId="mergeRules"
          openSection={openSection}
          onOpenSection={setOpenSection}
          title="4. Regras de Merge"
          className="gitflowSection methodAccordionItem"
        >
          <ol className="gitflowList">
            {mergeRules.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ol>
        </AccordionSection>
      

      
        <AccordionSection
          sectionId="cycleSummary"
          openSection={openSection}
          onOpenSection={setOpenSection}
          title="5. Resumo do Ciclo"
          className="gitflowSection methodAccordionItem"
        >
          <ol className="gitflowList">
            {cycleSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </AccordionSection>
      

      
        <AccordionSection
          sectionId="conventionalCommits"
          openSection={openSection}
          onOpenSection={setOpenSection}
          title="6. Conventional Commits"
          className="gitflowSection methodAccordionItem"
        >
          <p className="gitflowSectionSubtitle">Formato obrigatorio</p>
          <pre className="gitflowCodeBlock">{COMMIT_FORMAT}</pre>
          <p className="gitflowSectionSubtitle">Corpo recomendado</p>
          <pre className="gitflowCodeBlock">{COMMIT_BODY_TEMPLATE}</pre>
        </AccordionSection>
      

      
        <AccordionSection
          sectionId="commitTypes"
          openSection={openSection}
          onOpenSection={setOpenSection}
          title="7. Tipos Validos"
          className="gitflowSection methodAccordionItem"
        >
          <GitflowTable headers={["Tipo", "Uso"]} rows={commitTypes} />
        </AccordionSection>
      

      
        <AccordionSection
          sectionId="commitScopes"
          openSection={openSection}
          onOpenSection={setOpenSection}
          title="8. Escopos Validos para este projeto"
          className="gitflowSection methodAccordionItem"
        >
          <GitflowTable headers={["Escopo", "Area"]} rows={commitScopes} />
        </AccordionSection>
      

      
        <AccordionSection
          sectionId="commitExamples"
          openSection={openSection}
          onOpenSection={setOpenSection}
          title="9. Exemplos Reais"
          className="gitflowSection methodAccordionItem"
        >
          <pre className="gitflowCodeBlock">{COMMIT_EXAMPLES}</pre>
        </AccordionSection>
      

      
        <AccordionSection
          sectionId="commitTemplate"
          openSection={openSection}
          onOpenSection={setOpenSection}
          title="10. Template de Commit Detalhado"
          className="gitflowSection methodAccordionItem"
        >
          <pre className="gitflowCodeBlock">{DETAILED_COMMIT_TEMPLATE}</pre>
        </AccordionSection>
      

      
        <AccordionSection
          sectionId="qualityAutomation"
          openSection={openSection}
          onOpenSection={setOpenSection}
          title="11. Automacao de Qualidade"
          className="gitflowSection methodAccordionItem"
        >
          <pre className="gitflowCodeBlock">{QUALITY_COMMANDS}</pre>
        </AccordionSection>
      

      
        <AccordionSection
          sectionId="preCommitChecklist"
          openSection={openSection}
          onOpenSection={setOpenSection}
          title="12. Checklist rapido antes do commit"
          className="gitflowSection methodAccordionItem"
        >
          <ol className="gitflowList">
            {preCommitChecklist.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </AccordionSection>
      
    </div>
  );
}

